import { AsyncPipe, DecimalPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxDropdownOptions, OnyxSuggestion } from '@onyx/angular';
import {
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  onyxMinNumberValidator,
  OnyxSectionComponent,
  OnyxSuggestionsComponent,
  OnyxTabsComponent,
  OnyxTextFieldComponent,
} from '@onyx/angular';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { ValidationGroup } from '../../../../common/interfaces/validation/validation-group';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import { FleetCategory } from '../../common/enums/fleet-category';
import { UnitSuggestionsPipe } from '../../../../common/pipes/unit-suggestions.pipe';
import { UnitWarningPipe } from '../../../../common/pipes/unit-warning.pipe';

export type FleetCargoSpaceFormGroup = ReturnType<
  typeof FleetCargoSpaceFormComponent.buildForm
>;

@Component({
  selector: 'app-fleet-cargo-space-form',
  imports: [
    UnitWarningPipe,
    UnitSuggestionsPipe,
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxTextFieldComponent,
    OnyxDropdownComponent,
    OnyxTabsComponent,
    OnyxFormGroupComponent,
    OnyxSuggestionsComponent,
    AsyncPipe,
  ],
  templateUrl: './fleet-cargo-space-form.component.html',
  styleUrl: './fleet-cargo-space-form.component.scss',
})
export class FleetCargoSpaceFormComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly decimalPipe = inject(DecimalPipe);

  public readonly form = input.required<FleetCargoSpaceFormGroup>();
  public readonly validation = input.required<ValidationGroup>();
  public readonly category = input.required<FleetCategory>();

  protected readonly I18N = 'fleet.fleetForm.cargoSpace';
  protected readonly LENGTH_SUGGESTIONS = this.createDimensionSuggestions([
    1360,
  ]);
  protected readonly WIDTH_SUGGESTIONS = this.createDimensionSuggestions([
    245, 248,
  ]);
  protected readonly HEIGHT_SUGGESTIONS = this.createDimensionSuggestions([
    260, 300,
  ]);

  protected chambersNumbers$ = this.dictionariesService.getDictionary(
    DictionaryCode.CHAMBERS_NUMBER,
  );
  protected tankCertificates$ = this.dictionariesService.getDictionary(
    DictionaryCode.TANK_CERTIFICATE,
  );
  protected dischargeTypes = signal<OnyxDropdownOptions<string> | null>(null);

  protected isSemiTrailer = computed(
    () => this.category() === FleetCategory.SEMI_TRAILER,
  );
  protected hasTank = computed(
    () => this.validation().fields['tankCertificates'].type !== 'forbidden',
  );
  protected europalletsNumberSuggestions = computed(() => {
    const buildSuggestions = (keys: number[]) =>
      keys.map((key) => ({
        name: `${key}`,
        value: key,
      }));

    return {
      [FleetCategory.SEMI_TRUCK]: [],
      [FleetCategory.SEMI_TRAILER]: buildSuggestions([33]),
      [FleetCategory.TRAILER]: buildSuggestions([33]),
      [FleetCategory.STRAIGHT_TRUCK]: buildSuggestions([15, 18]),
      [FleetCategory.VAN]: buildSuggestions([8, 10, 12]),
    }[this.category()]!;
  });

  public ngOnInit(): void {
    effect(
      () => {
        const controls = this.form().controls;
        const fields = this.validation().fields;

        setTimeout(() => {
          ValidationHelper.updateControl(
            controls.volumeCapacity,
            fields['volumeCapacity'],
          );
          ValidationHelper.updateControl(
            controls.chambersNumber,
            fields['chambersNumber'],
          );
          ValidationHelper.updateControl(
            controls.tankCertificates,
            fields['tankCertificates'],
          );
          ValidationHelper.updateListControl(
            controls.dischargeTypes,
            fields['dischargeTypes'],
            this.translateService,
            this.dictionariesService,
            DictionaryCode.DISCHARGE_TYPE,
            this.dischargeTypes,
            true,
          );
          ValidationHelper.updateControl(
            controls.europalletsNumber,
            fields['europalletsNumber'],
          );
          ValidationHelper.updateControl(controls.length, fields['length']);
          ValidationHelper.updateControl(controls.width, fields['width']);
          ValidationHelper.updateControl(controls.height, fields['height']);
        });
      },
      { injector: this.injector },
    );
  }

  private createDimensionSuggestions(
    values: number[],
  ): OnyxSuggestion<number>[] {
    return values.map((value) => ({
      name: this.decimalPipe.transform(value / 100, '1.0-2')!,
      value,
    }));
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      loadCapacity: fb.control<number | null>(null, [
        Validators.required,
        onyxMinNumberValidator(1),
      ]),
      volumeCapacity: fb.control<number | null>(null, [
        onyxMinNumberValidator(Number.EPSILON, 2),
      ]),
      chambersNumber: fb.control<number | null>(null),
      tankCertificates: fb.control<string[] | null>(null),
      dischargeTypes: fb.control<string[] | null>(null),
      europalletsNumber: fb.control<number | null>(null, [
        onyxMinNumberValidator(1),
      ]),
      length: fb.control<number | null>(null, [
        onyxMinNumberValidator(Number.EPSILON, 2),
      ]),
      width: fb.control<number | null>(null, [
        onyxMinNumberValidator(Number.EPSILON, 2),
      ]),
      height: fb.control<number | null>(null, [
        onyxMinNumberValidator(Number.EPSILON, 2),
      ]),
    });
  }
}
